/**
 * Session adapter: bridges SavedSession to SavedEndpoint[] for the connection system
 *
 * All web-app sessions map to a single 'player' endpoint (backend role: phone).
 * The connection system (connectionFactory, connectionManager, activeConnectionsStore)
 * works with SavedEndpoint objects. This adapter converts sessions into that format.
 */

import type { SavedEndpoint } from '@/types/endpoint';
import type { SavedSession } from '@/types/session';

/**
 * Convert a saved session to an array of SavedEndpoint objects
 *
 * All sessions produce a player endpoint (backend role: phone).
 * IDs are deterministic based on session ID for consistent lookups.
 *
 * @param session - The saved session to convert
 * @returns Array of SavedEndpoint objects for the connection system
 */
export function sessionToEndpoints(session: SavedSession): SavedEndpoint[] {
  const { config, metadata } = session;

  const endpoint: SavedEndpoint = {
    id: `${session.id}--player`,
    name: config.sessionCode,
    type: 'player',
    credentials: {
      sessionCode: config.sessionCode,
      playerName: config.playerName
    },
    metadata: {
      createdAt: metadata.createdAt,
      lastUsedAt: metadata.lastUsedAt,
      totalRollsSubmitted: 0
    }
  };

  return [endpoint];
}

/**
 * Get all endpoint IDs derived from a session
 *
 * Useful for checking connection status or disconnecting all endpoints for a session.
 *
 * @param sessionId - The session ID to derive endpoint IDs from
 * @returns Array of deterministic endpoint ID strings
 */
export function getEndpointIdsForSession(sessionId: string): string[] {
  return [`${sessionId}--player`];
}
